import { createSlice } from "@reduxjs/toolkit";
import {
  getSession,
  getSessionRestart,
  getSessionResume,
  getSessionFinalize,
  getSessionReset,
  getSessionVerify,
  getSessionVerifyPn,
  getSessionResetVerifyPn,
} from "../actions/fluxoNSimilar";

interface Message {
  id?: string;
  sessionId?: string;
  message?: string;
  fromUser?: boolean;
  finalized?: boolean;
  createdAt?: string | null;
}

interface SessionInfo {
  message: Message | null | any;
  status: string;
}

interface SessionVerify {
  product: any;
  sessionId: any;
  finalized: any;
  description: any;
  httpStatus: any;
  pnNumber: any;
  pnNumberCode: any;
  infosGrid: any;
  isSimilar: any;
}

interface SessionCenter {
  products: any;
}

interface SessionState {
  sessionInfo: SessionInfo;
  sessionVerify: SessionVerify;
  sessionCenter: SessionCenter;
  status: "idle" | "loading" | "ready" | "error";
  error: any | null;
  httpStatus: number | null;
  messageStatus: any;
}

const initialState: SessionState = {
  sessionInfo: {
    message: null,
    status: "idle",
  },
  sessionCenter: {
    products: null,
  },
  sessionVerify: {
    sessionId: null,
    finalized: null,
    pnNumber: null,
    product: null,
    description: null,
    httpStatus: null,
    pnNumberCode: null,
    infosGrid: null,
    isSimilar: null,
  },
  status: "idle",
  error: { message: "" },
  httpStatus: null,
  messageStatus: null,
};

function buildSessionVerify(state: SessionState, payload: any) {
  const sessionData =
    payload?.message && Array.isArray(payload.message)
      ? payload.message[0]
      : null;

  if (sessionData) {
    Object.assign(state.sessionVerify, sessionData);
    state.sessionVerify.finalized = false;
  } else {
    state.sessionVerify.finalized = null;
  }

  state.sessionVerify.httpStatus = payload?.data ? 200 : 204;
  state.error = null;
}

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    resetSessionStatus(state) {
      state.status = "idle";
      state.httpStatus = null;
    },
    resetSession(state) {
      return initialState;
    },
    resetLoading(state) {
      state.sessionVerify.finalized = "";
    },
    setStatus(state, action) {
      state.httpStatus = action.payload.httpStatus;
      state.error = state.error || {};
      state.error.message = action.payload.message;
    },
    getPnCode(state, action) {
      state.sessionVerify.infosGrid = action.payload;
    },
    clearPnCode(state, action) {
      state.sessionVerify.infosGrid = null;
      state.sessionVerify.pnNumber = null;
    },

    clearSession(state) {
      state.sessionInfo.message = null;
      state.httpStatus = null;
      state.sessionVerify.isSimilar = null;
      state.sessionVerify.pnNumberCode = null;
    },

    clearStatus(state) {
      state.httpStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSession.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSession.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSession.fulfilled, (state, action) => {
        state.status = "ready";
        state.sessionInfo = action.payload;
        state.error = null;
        state.httpStatus = 200;
      })
      .addCase(getSessionVerify.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionVerify.rejected, (state, action) => {
        state.status = "error";
        state.error = action?.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })

      .addCase(getSessionVerify.fulfilled, (state, action) => {
        buildSessionVerify(state, action.payload);
      })

      .addCase(getSessionVerifyPn.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionVerifyPn.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionVerifyPn.fulfilled, (state, action) => {
        state.sessionVerify.pnNumber = true;
        state.sessionVerify.pnNumberCode =
          action.payload?.data?.product || null;
        state.sessionVerify.isSimilar = action.payload?.data?.isSimilar;
        state.sessionVerify.sessionId = action.payload?.data?.sessionId || null;

        buildSessionVerify(state, action.payload);
      })

      .addCase(getSessionResetVerifyPn.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionResetVerifyPn.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionResetVerifyPn.fulfilled, (state, action) => {
        state.sessionVerify.pnNumber = true;
        state.sessionVerify.pnNumberCode =
          action.payload?.data?.product || null;
        state.sessionVerify.isSimilar = action.payload?.data?.isSimilar;
        state.sessionVerify.sessionId = action.payload?.data?.sessionId || null;

        buildSessionVerify(state, action.payload);
      })

      .addCase(getSessionRestart.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionRestart.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionRestart.fulfilled, (state, action) => {
        state.status = "ready";
        state.sessionInfo = {
          ...state.sessionInfo,
          message: action.payload.message,
        };
        state.error = null;
        state.httpStatus = 200;
      })
      .addCase(getSessionResume.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionResume.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionResume.fulfilled, (state, action) => {
        state.status = "ready";
        state.sessionInfo = {
          ...state.sessionInfo,
          message: action.payload.message,
        };
        state.error = null;
        state.httpStatus = 200;
      })
      .addCase(getSessionFinalize.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionFinalize.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionFinalize.fulfilled, (state, action) => {
        state.status = "idle";
        state.sessionInfo = {
          ...state.sessionInfo,
          message: action.payload.message,
        };
        state.error = null;
        state.httpStatus = 200;
      })
      
      .addCase(getSessionReset.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.httpStatus = null;
      })
      .addCase(getSessionReset.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload;
        state.httpStatus = action.payload?.httpStatus || null;
        state.messageStatus = action.error.message;
      })
      .addCase(getSessionReset.fulfilled, (state, action) => {
        state.status = "idle";
        state.sessionInfo = {
          ...state.sessionInfo,
          message: action.payload.message,
        };
        state.error = null;
        state.httpStatus = 200;
      });
  },
});

export const {
  resetSessionStatus,
  resetSession,
  resetLoading,
  setStatus,
  getPnCode,
  clearPnCode,
  clearSession,
  clearStatus,
} = sessionSlice.actions;
export default sessionSlice.reducer;
